<script lang="ts">
  import { i18n } from "../i18n.svelte";
  import { inview, smoothScroll } from "../actions";
  import PhoneFrame from "./mockups/PhoneFrame.svelte";
  import ScreenPractice from "./mockups/ScreenPractice.svelte";
  import Sprout from "./Sprout.svelte";

  const h = $derived(i18n.t.hero);
</script>

<section class="hero" id="top">
  <div class="container grid">
    <div class="copy">
      <span class="chip reveal" use:inview><span class="dot"></span>{h.chip}</span>
      <p class="kicker reveal" use:inview={{ delay: 80 }}>{h.kicker}</p>

      <h1>
        <span class="reveal ln" use:inview={{ delay: 140 }}>{h.titleTop}</span>
        <span class="reveal ln em" use:inview={{ delay: 220 }}>
          {h.titleEm}<svg class="underline" viewBox="0 0 220 14" preserveAspectRatio="none" aria-hidden="true"><path d="M3 8 C 60 13 160 2 217 7" stroke="var(--sprout)" stroke-width="5" fill="none" stroke-linecap="round"/></svg>
        </span>
        <span class="reveal ln" use:inview={{ delay: 300 }}>{h.titleBottom}</span>
      </h1>

      <p class="lead reveal" use:inview={{ delay: 380 }}>{h.lead}</p>

      <div class="ctas reveal" use:inview={{ delay: 460 }}>
        <a href="#cta" class="btn btn-primary" use:smoothScroll>
          {h.ctaPrimary}<span class="arrow">→</span>
        </a>
        <a href="#demo" class="btn btn-ghost" use:smoothScroll>{h.ctaSecondary}</a>
      </div>

      <ul class="stats reveal" use:inview={{ delay: 540 }}>
        {#each h.miniStats as s}
          <li>
            <strong>{s.value}</strong>
            <span>{s.label}</span>
          </li>
        {/each}
      </ul>
    </div>

    <div class="visual reveal" use:inview={{ delay: 260 }}>
      <span class="leaf leaf-a"><Sprout size={56} stage={3} /></span>
      <span class="leaf leaf-b"><Sprout size={40} stage={4} /></span>

      <PhoneFrame label="Tane — adaptive practice screen">
        <ScreenPractice />
      </PhoneFrame>

      <div class="float tutor">
        <span class="ava"><Sprout size={18} stage={2} /></span>
        <div>
          <strong>AI {i18n.locale === "ja" ? "チューター" : "tutor"}</strong>
          <span>{i18n.locale === "ja" ? "24時間いつでも" : "always on, 24/7"}</span>
        </div>
      </div>

      <div class="float grow">
        <Sprout size={26} stage={4} />
        <div>
          <strong>+68%</strong>
          <span>{i18n.locale === "ja" ? "習得度" : "mastery"}</span>
        </div>
      </div>
    </div>
  </div>
</section>

<style>
  .hero {
    position: relative;
    padding-block: clamp(2.5rem, 1rem + 6vw, 6rem) clamp(3.5rem, 2rem + 6vw, 7rem);
    overflow: hidden;
  }
  .grid {
    display: grid;
    grid-template-columns: 1.08fr 0.92fr;
    gap: clamp(2rem, 1rem + 4vw, 5rem);
    align-items: center;
  }

  .chip {
    display: inline-flex;
    align-items: center;
    gap: 0.5em;
    font-family: var(--font-mono);
    font-size: 0.72rem;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--green-deep);
    background: var(--sprout-soft);
    border: 1px solid rgba(95, 179, 100, 0.4);
    padding: 0.45em 0.9em;
    border-radius: 999px;
  }
  .chip .dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--green);
    animation: pulse 2.6s var(--ease) infinite;
  }
  .kicker {
    margin-top: 1.4rem;
  }

  h1 {
    margin-top: 1.1rem;
    font-size: var(--fs-display);
    line-height: 1.04;
  }
  :global(:lang(ja)) h1 {
    line-height: 1.22;
  }
  .ln {
    display: block;
  }
  .em {
    position: relative;
    color: var(--green-deep);
    width: fit-content;
  }
  .underline {
    position: absolute;
    left: 0;
    right: 0;
    bottom: -0.12em;
    width: 100%;
    height: 0.42em;
    overflow: visible;
  }

  .lead {
    margin-top: 1.8rem;
    max-width: 46ch;
    font-size: var(--fs-lead);
    color: var(--ink-soft);
    line-height: 1.9;
  }

  .ctas {
    margin-top: 2rem;
    display: flex;
    flex-wrap: wrap;
    gap: 0.8rem;
  }

  .stats {
    margin-top: 2.4rem;
    display: flex;
    flex-wrap: wrap;
    gap: clamp(1.2rem, 0.5rem + 2vw, 2.5rem);
    padding-top: 1.6rem;
    border-top: 1px solid var(--line);
  }
  .stats strong {
    display: block;
    font-family: var(--font-display);
    font-size: 1.5rem;
    font-weight: 600;
    color: var(--ink);
    line-height: 1.1;
  }
  .stats span {
    font-size: 0.8rem;
    color: var(--muted);
  }

  /* visual */
  .visual {
    position: relative;
    display: flex;
    justify-content: center;
  }
  .leaf {
    position: absolute;
    z-index: 0;
    opacity: 0.85;
  }
  .leaf-a {
    top: -1.4rem;
    left: 0.5rem;
    transform: rotate(-18deg);
    animation: sway 7s var(--ease) infinite;
  }
  .leaf-b {
    bottom: 1rem;
    right: -0.5rem;
    transform: rotate(14deg);
    animation: sway 8s var(--ease) infinite reverse;
  }
  @keyframes sway {
    0%, 100% { transform: rotate(-16deg) translateY(0); }
    50% { transform: rotate(-10deg) translateY(-6px); }
  }

  .float {
    position: absolute;
    z-index: 4;
    display: flex;
    align-items: center;
    gap: 0.55rem;
    background: var(--paper-card);
    border: 1px solid var(--line);
    border-radius: 14px;
    padding: 0.6rem 0.85rem;
    box-shadow: var(--shadow-soft);
  }
  .float strong {
    display: block;
    font-size: 0.86rem;
    color: var(--ink);
  }
  .float span {
    font-size: 0.72rem;
    color: var(--muted);
  }
  .float .ava {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background: var(--sprout-soft);
    display: grid;
    place-items: center;
  }
  .tutor {
    top: 14%;
    left: -1.5rem;
    animation: bob 6s var(--ease) infinite;
  }
  .grow {
    bottom: 16%;
    right: -1.2rem;
    animation: bob 6s var(--ease) infinite 1.5s;
  }
  @keyframes bob {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-7px); }
  }

  @media (max-width: 920px) {
    .grid {
      grid-template-columns: 1fr;
    }
    .visual {
      order: -1;
      margin-bottom: 1rem;
    }
    .tutor {
      left: 0;
    }
    .grow {
      right: 0;
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .leaf, .float {
      animation: none;
    }
  }
</style>
